import { HelpCircle } from "lucide-react";
import { Link } from "react-router-dom";
import PageHeader from "../components/PageHeader";
import {
  Accordion, AccordionItem, AccordionTrigger, AccordionContent,
} from "../components/ui/accordion";

const SECTIONS = [
  {
    group: "Haplap & our brand",
    items: [
      {
        q: "What is Haplap?",
        a: "Haplap is a dignity-first community platform where anyone can ask for help without judgment, and kind people can find honest neighbours to support. The name comes from the old corner-store penny dish: \"Have A Penny, Leave A Penny\" — HAPLAP.",
      },
      {
        q: "Where did the idea come from?",
        a: "From the little dish of pennies, nickels and dimes that used to sit by the till at every corner store — with a sign that said \"Have a penny, leave a penny. Need a penny, take a penny.\" It was a quiet promise that neighbours look out for one another. You can read the full story on our About page.",
      },
      {
        q: "How is Haplap different from GoFundMe or social media fundraisers?",
        a: "Two big ways. First, we moderate hard — there is zero tolerance for cruelty, so people can ask for help without being ridiculed. Second, our Community Tip Jar shares giving evenly, so nobody who asks is ever left with nothing while someone else raises thousands.",
      },
      {
        q: "Is Haplap a registered charity?",
        a: "No. Haplap is a community platform that connects people who need help directly with people who want to help. Donations go directly to individuals, not to a charity's general fund.",
      },
    ],
  },
  {
    group: "Accounts: Members & Supporters",
    items: [
      {
        q: "What is a Member (Neighbor)?",
        a: "A Member is someone who may receive support. Members have a public profile with their story, needs, goals and photos, and can receive tips and recurring pledges. This is the right choice if you're asking for help or sharing your journey.",
      },
      {
        q: "What is a Supporter?",
        a: "A Supporter is someone who is here mainly to give. Supporter accounts are private by default — hidden from Discovery and search — and giving can be anonymous. Choose this if you'd rather quietly help others without a public profile.",
      },
      {
        q: "Can I switch between the two?",
        a: "Yes. You can change your account mode and privacy settings any time from your profile editor.",
      },
      {
        q: "How do I sign up?",
        a: "Use the Sign in button and register with your email and a password, or continue with Google. It only takes a moment.",
      },
    ],
  },
  {
    group: "Giving: tips, the jar & pledges",
    items: [
      {
        q: "How do direct tips work?",
        a: "You can send a one-time tip to any Member from their profile. The recipient keeps 100% of the amount you choose to give them — the small platform fee and card processing are added on top and covered by you, the donor, so the person you're helping receives the full amount.",
      },
      {
        q: "What is the Community Tip Jar?",
        a: "The jar is our answer to the heartbreak of watching one person raise thousands while another gets nothing. When you contribute to the jar, your gift is split evenly across everyone currently asking for help — so even people you've never met receive a share, and nobody is left feeling that no one cares.",
      },
      {
        q: "When does the jar get shared out?",
        a: "The jar's balance is distributed evenly on a regular daily split among the people currently seeking help. The big \"in the jar\" number is the current balance; \"given so far\" is the total that has already been shared out.",
      },
      {
        q: "What are recurring monthly pledges?",
        a: "You can set up a monthly pledge to keep supporting someone over time, not just once. You can cancel a recurring pledge whenever you like.",
      },
      {
        q: "Can I give anonymously?",
        a: "Yes. When you give, you can hide your name publicly (the recipient can still thank you), or give fully anonymously so you're hidden everywhere, even from the recipient.",
      },
    ],
  },
  {
    group: "Wallet & cashing out",
    items: [
      {
        q: "What is my Wallet?",
        a: "Your Wallet holds the support you've received and shows a full ledger of every tip, jar share, pledge and gift-card redemption — in and out. Members use it to track and withdraw the help they receive.",
      },
      {
        q: "How do I withdraw the money I've received?",
        a: "From your Wallet you can request a cash-out to be paid to you. Once funds leave the platform to you, they're yours to use for the help you need.",
      },
      {
        q: "Does the recipient really keep 100%?",
        a: "Yes. For direct tips, the donor covers the platform fee and processing on top, so the full amount you were given lands in your wallet.",
      },
    ],
  },
  {
    group: "Neighbors in Need (local groups)",
    items: [
      {
        q: "What is Neighbors in Need?",
        a: "Private, local groups where neighbours pool money and gift cards into a shared fund to help a specific person or family, or to run a community program like a block party, Easter egg hunt, or back-to-school drive.",
      },
      {
        q: "How do I join a group?",
        a: "Groups are invite-only. Ask an organizer for the join code and enter it on the Neighbors page.",
      },
      {
        q: "Who can create a group?",
        a: "To keep groups genuine, you can create one once you've supported at least five different people on Haplap (admins can always create). This makes sure organizers are active, trusted members of the community.",
      },
      {
        q: "What can a group do?",
        a: "Pool a shared fund toward a goal, nominate a beneficiary or run a program, load reusable digital gift cards, chat privately, and share a photo album of your events.",
      },
    ],
  },
  {
    group: "Community, chat & the Shop",
    items: [
      {
        q: "What is the Community Room?",
        a: "A friendly, moderated space with topic channels (support, housing, jobs, wins, local and more) where the community talks, shares wins, sends encouragement, reactions and gifts in real time.",
      },
      {
        q: "Can I send private messages?",
        a: "Yes. You can direct-message other members from the Messages page, unless someone has turned off private messages or there is a block in place.",
      },
      {
        q: "What is the Shop for?",
        a: "The Shop offers playful sticker packs and heartfelt gifts you can send in the Community Room. Purchases help keep Haplap running for the people who need it.",
      },
    ],
  },
  {
    group: "Safety, moderation & Guardians",
    items: [
      {
        q: "How does Haplap keep people safe?",
        a: "We have a zero-tolerance policy for harassment and cruelty. Messages, posts and comments are automatically screened; mild abuse triggers warnings, and severe harassment or slurs result in an immediate permanent ban that also blocks re-registration.",
      },
      {
        q: "Who are Community Guardians?",
        a: "Guardians are trusted, admin-approved members who help keep the community kind. They can remove harmful content and issue a 24-hour cool-down to a user. Repeated cool-downs escalate to an admin review and possible ban.",
      },
      {
        q: "How do I report someone?",
        a: "You can report any post, comment or chat message, and block anyone directly. Reports go straight to our moderation team and Guardians.",
      },
      {
        q: "How do I become a Guardian?",
        a: "Active, kind members can apply to become a Guardian; an admin reviews every application.",
      },
    ],
  },
  {
    group: "Fees, payments & refunds",
    items: [
      {
        q: "What does Haplap charge?",
        a: "We add a small platform fee plus payment processing on top of a gift — the donor covers it so recipients keep 100%. That fee is not spent on executive salaries; it funds building and growing the platform so we can help more people, everywhere.",
      },
      {
        q: "What payment methods can I use?",
        a: "We process payments securely through Stripe. Additional methods are being added over time.",
      },
      {
        q: "Can I get a refund?",
        a: "Donations and tips are final and non-refundable, because 100% goes to the individual and is often withdrawn or shared through the jar quickly — once it reaches the person who needed it, we have no way to recover it. We do review genuine disputes (for example suspected fraud) case-by-case. See our Refund Policy for full details.",
      },
    ],
  },
];

export default function FAQ() {
  return (
    <div data-testid="faq-page" className="max-w-3xl mx-auto px-4 py-8 sm:py-12 w-full flex flex-col items-center">
      <PageHeader
        icon={HelpCircle}
        eyebrow="Help Center"
        title="Frequently asked questions"
        subtitle="Everything about how Haplap works — giving, receiving, safety, and our promise to you."
      />

      <div className="w-full space-y-6">
        {SECTIONS.map((section, si) => (
          <div key={si} className="w-full max-w-2xl mx-auto">
            <h2 className="font-serif text-xl sm:text-2xl font-semibold text-bronze text-center mb-3">
              {section.group}
            </h2>
            <div className="rounded-3xl bg-white border border-border/80 shadow-sm px-5 sm:px-7">
              <Accordion type="single" collapsible className="w-full">
                {section.items.map((item, ii) => (
                  <AccordionItem key={ii} value={`s${si}-i${ii}`} data-testid={`faq-item-${si}-${ii}`}>
                    <AccordionTrigger className="text-sm sm:text-base font-medium text-foreground hover:no-underline hover:text-copper">
                      {item.q}
                    </AccordionTrigger>
                    <AccordionContent className="text-sm sm:text-base text-foreground/80 leading-relaxed">
                      {item.a}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </div>
        ))}
      </div>

      <div className="w-full max-w-2xl mx-auto text-center mt-10 rounded-3xl bg-cream/60 border border-border/70 p-7 space-y-2">
        <p className="font-serif text-lg text-bronze">Still have a question?</p>
        <p className="text-sm text-foreground/75">
          We're always happy to help. Reach us at{" "}
          <a href="mailto:support@haplap.ca" className="text-copper font-medium underline" data-testid="faq-support-email">
            support@haplap.ca
          </a>{" "}
          — or read more <Link to="/about" className="text-copper font-medium underline">about our mission</Link>.
        </p>
      </div>
    </div>
  );
}
